import React, { useMemo } from 'react';
import ArrearsSC, { EmptyArrearsSC, DataArrearsSC } from './arrears.styled';
import { ReactComponent as CheckCircle } from '../../../assets/img/svg/check-circle.svg';
import { BodyText, HeadingFour, BiggerText } from '../../typography';
import PropTypes from 'prop-types';
import SmallText from '../../typography/small-text';
// import Button from '../../ui/button';
import { FormattedMessage } from 'react-intl';
import dlt from '../../../utils/default-lang';
import numeral from 'numeral';
import Moment from 'react-moment';
import moment from 'moment';

const Arrears = ({
  isEmpty,
  daysInArrears,
  actualUnpaid,
  overpaid,
  schedules,
  receivables,
  communicationProcessingDate,
  forgiven,
}) => {
  const inArrears = useMemo(() => schedules.filter((loan) => loan[12] > 0), [schedules]);
  const inArrearsTotal = useMemo(() => {
    return inArrears.reduce((acc, curr) => {
      return acc + parseFloat(curr[12]);
    }, 0);
  }, [inArrears]);
  const getTotalSum = React.useCallback(
    (key) => {
      const values = forgiven.filter((item) => item.type === key);
      return values.reduce((acc, curr) => {
        return acc + parseFloat(curr.value);
      }, 0);
    },
    [forgiven]
  );

  return (
    <ArrearsSC isEmpty={isEmpty}>
      {isEmpty ? (
        <EmptyArrearsSC>
          <CheckCircle />
          <BiggerText className="no-arrears">
            <FormattedMessage
              id="borrower_dash_arrears_no_arrears"
              defaultMessage={dlt['borrower_dash_arrears_no_arrears']}
            />
          </BiggerText>
        </EmptyArrearsSC>
      ) : (
        <DataArrearsSC>
          <div className="headline-data-wrap">
            <BodyText className="bolder">
              <FormattedMessage
                id="borrower_dash_arrears_headline"
                defaultMessage={dlt['borrower_dash_arrears_headline']}
              />
              <span className="processing-date">{communicationProcessingDate}</span>
            </BodyText>
            <BodyText>
              <FormattedMessage
                id="borrower_dash_arrears_delay"
                defaultMessage={dlt['borrower_dash_arrears_delay']}
              />{' '}
              <b>
                {daysInArrears === 1 ? (
                  <>
                    {daysInArrears}{' '}
                    <FormattedMessage id="global_day" defaultMessage={dlt['global_day']} />
                  </>
                ) : daysInArrears > 4 || daysInArrears === 0 ? (
                  <>
                    {daysInArrears}{' '}
                    <FormattedMessage id="global_days" defaultMessage={dlt['global_days']} />
                  </>
                ) : (
                  <>
                    {daysInArrears}{' '}
                    <FormattedMessage
                      id="global_more_days"
                      defaultMessage={dlt['global_more_days']}
                    />{' '}
                  </>
                )}
              </b>
            </BodyText>
          </div>
          {inArrears.length === 0 ? null : (
            <>
              <div className="body-data-wrap">
                <div className="body-data-wrap--item">
                  <SmallText className="black-transparent">
                    <FormattedMessage
                      id="borrower_dash_arrears_in_arrears"
                      defaultMessage={dlt['borrower_dash_arrears_in_arrears']}
                    />
                  </SmallText>

                  {inArrears.map((loan) => {
                    return (
                      <div
                        key={loan[0]}
                        className={
                          loan[29] !== '' || loan[30] !== '' || loan[23] !== '' || loan[24] !== ''
                            ? 'full-arrear'
                            : ''
                        }
                      >
                        <div className="body-data-wrap--item__body-row">
                          <BodyText className="bolder">
                            <Moment parse="DD.MM.YYYY" format="DD. MMMM YYYY">
                              {loan[1]}
                            </Moment>
                          </BodyText>
                          <BodyText className="bolder red">
                            {numeral(
                              Number(loan[13]) +
                                Number(loan[14]) +
                                Number(loan[15]) +
                                Number(loan[16])
                            ).format('0,00.00')}{' '}
                            €
                          </BodyText>
                        </div>
                        {loan[23] !== '' || loan[24] !== '' ? (
                          <div className="body-data-wrap--item__body-row padded-small">
                            <SmallText>
                              <FormattedMessage
                                id="borrower_dash_arrears_interest_late"
                                defaultMessage={dlt['borrower_dash_arrears_interest_late']}
                              />
                            </SmallText>
                            <SmallText className="red">
                              {numeral(Number(loan[23]) + Number(loan[24])).format('0,00.00')} €
                            </SmallText>
                          </div>
                        ) : null}

                        {loan[29] !== '' || loan[30] !== '' ? (
                          <div className="body-data-wrap--item__body-row padded-small">
                            <SmallText>
                              <FormattedMessage
                                id="borrower_dash_arrears_contractual_penalty"
                                defaultMessage={dlt['borrower_dash_arrears_contractual_penalty']}
                              />
                            </SmallText>
                            <SmallText className="red">
                              {numeral(Number(loan[29]) + Number(loan[30])).format('0,00.00')} €
                            </SmallText>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="summary-wrap">
                <div className="summary-wrap--amount">
                  <>
                    <BiggerText>
                      <FormattedMessage
                        id="borrower_dash_arrears_installments"
                        defaultMessage={dlt['borrower_dash_arrears_installments']}
                      />
                    </BiggerText>
                    <HeadingFour className="red">
                      {numeral(inArrearsTotal).format('0,0.00')} €
                    </HeadingFour>
                  </>
                </div>
              </div>
            </>
          )}

          {receivables.sum > 0 ? (
            <>
              <div className="body-data-wrap">
                <div className="body-data-wrap--item">
                  <SmallText className="black-transparent">
                    <FormattedMessage
                      id="borrower_dash_arrears_other_payments"
                      defaultMessage={dlt['borrower_dash_arrears_other_payments']}
                    />
                  </SmallText>
                  <div className="body-data-wrap--item__body-row">
                    <BodyText className="bolder">
                      <FormattedMessage
                        id="borrower_dash_arrears_emails"
                        defaultMessage={dlt['borrower_dash_arrears_emails']}
                      />
                    </BodyText>
                    <BodyText className="bolder">
                      {numeral(Number(receivables.email)).format('0,00.00')} €
                    </BodyText>
                  </div>
                  <div className="body-data-wrap--item__body-row">
                    <BodyText className="bolder">
                      <FormattedMessage
                        id="borrower_dash_arrears_sms"
                        defaultMessage={dlt['borrower_dash_arrears_sms']}
                      />
                    </BodyText>
                    <BodyText className="bolder">
                      {numeral(Number(receivables.sms)).format('0,00.00')} €
                    </BodyText>
                  </div>
                  <div className="body-data-wrap--item__body-row">
                    <BodyText className="bolder">
                      <FormattedMessage
                        id="borrower_dash_arrears_calls"
                        defaultMessage={dlt['borrower_dash_arrears_calls']}
                      />
                    </BodyText>
                    <BodyText className="bolder">
                      {numeral(Number(receivables.call)).format('0,00.00')} €
                    </BodyText>
                  </div>
                  <div className="body-data-wrap--item__body-row">
                    <BodyText className="bolder">
                      <FormattedMessage
                        id="borrower_dash_arrears_letters"
                        defaultMessage={dlt['borrower_dash_arrears_letters']}
                      />
                    </BodyText>
                    <BodyText className="bolder">
                      {numeral(Number(receivables.letter)).format('0,00.00')} €
                    </BodyText>
                  </div>
                </div>
              </div>
              <div className="summary-wrap">
                <div className="summary-wrap--amount">
                  <>
                    <BiggerText>
                      <FormattedMessage
                        id="borrower_dash_arrears_other_payments_total"
                        defaultMessage={dlt['borrower_dash_arrears_other_payments_total']}
                      />
                    </BiggerText>

                    <HeadingFour className={Number(receivables.sum) === 0 ? null : 'red'}>
                      {numeral(Number(receivables.sum)).format('0,0.00')} €
                    </HeadingFour>
                  </>
                </div>
              </div>
            </>
          ) : null}
          {forgiven.length ? (
            <>
              <div className="body-data-wrap">
                <div className="body-data-wrap--item">
                  <SmallText className="black-transparent">
                    <FormattedMessage
                      id="borrower_dash_arrears_forgiven_payments"
                      defaultMessage={dlt['borrower_dash_arrears_forgiven_payments']}
                    />
                  </SmallText>
                  <div className="body-data-wrap--item__body-row">
                    <BodyText className="bolder">
                      <FormattedMessage
                        id="borrower_dash_arrears_emails"
                        defaultMessage={dlt['borrower_dash_arrears_emails']}
                      />
                    </BodyText>
                    <BodyText className="bolder">
                      {numeral(Number(getTotalSum('email'))).format('0,00.00')} €
                    </BodyText>
                  </div>
                  <div className="body-data-wrap--item__body-row">
                    <BodyText className="bolder">
                      <FormattedMessage
                        id="borrower_dash_arrears_sms"
                        defaultMessage={dlt['borrower_dash_arrears_sms']}
                      />
                    </BodyText>
                    <BodyText className="bolder">
                      {numeral(Number(getTotalSum('sms'))).format('0,00.00')} €
                    </BodyText>
                  </div>
                  <div className="body-data-wrap--item__body-row">
                    <BodyText className="bolder">
                      <FormattedMessage
                        id="borrower_dash_arrears_calls"
                        defaultMessage={dlt['borrower_dash_arrears_calls']}
                      />
                    </BodyText>
                    <BodyText className="bolder">
                      {numeral(Number(getTotalSum('call'))).format('0,00.00')} €
                    </BodyText>
                  </div>
                  <div className="body-data-wrap--item__body-row">
                    <BodyText className="bolder">
                      <FormattedMessage
                        id="borrower_dash_arrears_letters"
                        defaultMessage={dlt['borrower_dash_arrears_letters']}
                      />
                    </BodyText>
                    <BodyText className="bolder">
                      {numeral(Number(getTotalSum('letter'))).format('0,00.00')} €
                    </BodyText>
                  </div>
                </div>
              </div>
              <div className="summary-wrap">
                <div className="summary-wrap--amount">
                  <>
                    <BiggerText>
                      <FormattedMessage
                        id="borrower_dash_arrears_forgiven_payments_total"
                        defaultMessage={dlt['borrower_dash_arrears_forgiven_payments_total']}
                      />
                    </BiggerText>

                    <HeadingFour className="green">
                      {numeral(
                        Number(getTotalSum('email')) +
                          Number(getTotalSum('sms')) +
                          Number(getTotalSum('call')) +
                          Number(getTotalSum('letter'))
                      ).format('0,0.00')}{' '}
                      €
                    </HeadingFour>
                  </>
                </div>
              </div>
            </>
          ) : null}

          <div className="body-data-wrap">
            <div className="body-data-wrap--item">
              <SmallText className="black-transparent">
                <FormattedMessage
                  id="borrower_dash_arrears_total_loan_status"
                  defaultMessage={dlt['borrower_dash_arrears_total_loan_status']}
                />
                <span className="bolder processing-date">
                  <SmallText className="bolder">
                    {' '}
                    {moment(new Date()).format('DD.MM.YYYY')}
                  </SmallText>
                </span>
              </SmallText>
              {actualUnpaid ? (
                <div className="body-data-wrap--item__body-row">
                  <BodyText className="bolder red">
                    <FormattedMessage
                      id="borrower_dash_arrears_total_loan_status_underpaid"
                      defaultMessage={dlt['borrower_dash_arrears_total_loan_status_underpaid']}
                    />
                  </BodyText>
                  <BodyText className="bolder red">
                    {numeral(Number(actualUnpaid)).format('0,00.00')} €
                  </BodyText>
                </div>
              ) : null}
              {overpaid ? (
                <div className="body-data-wrap--item__body-row">
                  <BodyText className="bolder green">
                    <FormattedMessage
                      id="borrower_dash_arrears_total_loan_status_overpaid"
                      defaultMessage={dlt['borrower_dash_arrears_total_loan_status_overpaid']}
                    />
                  </BodyText>
                  <BodyText className="bolder green">
                    {numeral(Number(overpaid)).format('0,00.00')} €
                  </BodyText>
                </div>
              ) : null}
            </div>
          </div>
        </DataArrearsSC>
      )}
    </ArrearsSC>
  );
};

Arrears.propTypes = {
  isEmpty: PropTypes.bool,
};
Arrears.defaultProps = {
  isEmpty: true,
};

export default Arrears;
